import { useCallback, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { User } from 'api/models';
import { Project } from 'api/project';
import { Action, EventType, ObjectType, SocketData } from 'api/pushes';
import {
  ResourceType,
  SocketDataForTeam,
  SocketDataForTeamInRm,
} from 'api/resource-management/types';
import { InvitedMembers } from 'data/members/slice';
import { useIsCurrentUserCollaborator } from 'hooks/permissions';
import { useAddTeamToProjectWSHandler } from 'hooks/use-add-team-to-project-ws-handler';
import { ALL_PROJECTS_QUERY_KEY } from 'queries/constants';
import { useAddProjectUserLiteToQueryState } from 'queries/project';
import { logger } from 'services';
import { useAddRemoveResource } from '../../resource-manager';
import { useMultiProjectWsUpdatesInRM } from './multi-project-ws-manager';
import { ProjectMembersWsProps } from './project-members-socket-manager';

export const useProjectMembersCreateWSManager = (
  props: ProjectMembersWsProps,
): void => {
  const queryClient = useQueryClient();

  const { userLookup, teamLookup, projectChannels } = props;

  const { addResource } = useAddRemoveResource(props);
  const addTeamToProjectSocketHandler = useAddTeamToProjectWSHandler();
  const addProjectUserLiteToQueryState = useAddProjectUserLiteToQueryState();
  const isCollaborator = useIsCurrentUserCollaborator();
  const addHandler = useRef<(socketData: SocketData<string>) => void>();
  const addMemberV1Handler =
    useRef<
      (socketData: SocketData<(InvitedMembers | SocketDataForTeam)[]>) => void
    >();

  const addUserHandler = useCallback(
    (projectId: number, data: InvitedMembers) => {
      queryClient.setQueryData<Project[]>(
        ALL_PROJECTS_QUERY_KEY,
        (previous) =>
          previous?.map((project) => {
            if (project.projectId === projectId) {
              return {
                ...project,
                teamMembers: [
                  ...(project.teamMembers ?? []).filter(
                    ({ userId }) => userId !== data.user.userId,
                  ),
                  ...[data.user as User],
                ],
              };
            }

            return project;
          }) ?? [],
      );
      if (data.user.userId) {
        if (data.referenceTeam == null && data.role == null) {
          addProjectUserLiteToQueryState(projectId, {
            user: { userId: data.user.userId },
          });
          addResource({
            projectId,
            resource: {
              type: ResourceType.User,
              user: userLookup[data.user.userId],
            },
            options: {
              highlight: false,
            },
          });
        }
      }
    },
    [addProjectUserLiteToQueryState, addResource, queryClient, userLookup],
  );

  const addTeamHandler = useCallback(
    (projectId: number, data: SocketDataForTeamInRm) => {
      const team = addTeamToProjectSocketHandler(
        projectId,
        data.team,
        teamLookup,
      );

      if (team != null) {
        addResource({
          projectId,
          resource: {
            type: ResourceType.Team,
            team,
          },
          options: {
            highlight: false,
            expand: false,
          },
        });
      }
    },
    [addResource, addTeamToProjectSocketHandler, teamLookup],
  );

  const handleMembersAdd = useCallback(
    (socketData: SocketData<string>) => {
      logger.log('project-members-create ~ socketData', socketData);
      try {
        const { data, projectId } = socketData;
        const members: (InvitedMembers | SocketDataForTeamInRm)[] =
          JSON.parse(data);

        members.forEach((item) => {
          if ((item as InvitedMembers).user != null) {
            addUserHandler(projectId, item as InvitedMembers);
          } else if ((item as SocketDataForTeamInRm).team != null) {
            if (!isCollaborator) {
              addTeamHandler(projectId, item as SocketDataForTeamInRm);
            }
          }
        });
      } catch (error) {
        logger.error('Error while adding project members', { error });
      }
    },
    [addTeamHandler, addUserHandler, isCollaborator],
  );

  addHandler.current = handleMembersAdd;

  const addHandlerProxy = useCallback((params) => {
    addHandler.current?.(params);
  }, []);
  const handleMembersV1Add = useCallback(
    (socketData: SocketData<(InvitedMembers | SocketDataForTeam)[]>) => {
      const { data: members, projectId } = socketData;

      members.forEach((item) => {
        if (
          (item as InvitedMembers).user &&
          (item as InvitedMembers).referenceTeam == null
        ) {
          addUserHandler(projectId, item as InvitedMembers);
        }
      });
    },
    [addUserHandler],
  );

  addMemberV1Handler.current = handleMembersV1Add;

  const addMemberV1HandlerProxy = useCallback((params) => {
    addMemberV1Handler.current?.(params);
  }, []);

  useMultiProjectWsUpdatesInRM(
    projectChannels,
    addHandlerProxy,
    EventType(Action.Create, ObjectType.ProjectMembersV2),
  );
  useMultiProjectWsUpdatesInRM(
    projectChannels,
    addMemberV1HandlerProxy,
    EventType(Action.Create, ObjectType.ProjectMembers),
  );
};
